import { ClassKey } from '@const/class.constants';
import { CHART_TYPE } from '@const/pageResponse.constants';
import { SubsystemsResponseDTO } from '@dto/architecture.dto';
import { ItemGroupWithLabels } from '@dto/customGroup.dto';
import {
  CreateDashboardCustomGraphDTO,
  CustomGraphMetric,
  DashboardCustomGraph,
  DashboardCustomGraphPreview,
} from '@dto/dashboard.dto';
import { ChartType, GraphInterval, MetricAggregate } from '@dto/pageResponse.dto';
import type { FormInstance } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import type { NamePath } from 'antd/lib/form/interface';
import { AxiosResponse } from 'axios';
import type { ReactNode } from 'react';
import { defaultRule, formatRuleDataForApi, ruleHwType } from 'src/component/selectItems/XmRule';
import { RuleGroup } from 'src/component/selectItems/XmRuleList';
import { MenuNode } from 'src/model/MenuNode';

export type SubsPromise = Promise<AxiosResponse<SubsystemsResponseDTO>>;

// GB picker tree node; title may be JSX (icons/highlight) and shared-branch nodes carry
// group_id (inherited). group_root marks the one node that owns the branch.
export type GbTreeNode = Omit<DefaultOptionType, 'title'> & { title?: ReactNode; group_id?: number; group_root?: boolean; };

export const GROUPS_DIVIDER_KEY = MenuNode.GROUPS_DIVIDER_KEY;
export const GROUPS_DIVIDER_LABEL = '---------- Group Dashboards ----------';

export function resetFormTouched(form: FormInstance) {
  const values = form.getFieldsValue(true);
  const fields: { name: NamePath; touched: boolean; }[] = [];
  const walk = (ref: unknown, path?: (string | number)[]) => {
    if (!ref) return;
    if (Array.isArray(ref)) {
      ref.forEach((r, i) => {
        const next: (string | number)[] = [...(path ?? []), i];
        fields.push({ name: next, touched: false });
        walk(r, next);
      });
      return;
    }
    if (typeof ref === 'object') {
      Object.keys(ref).forEach(k => {
        const next: (string | number)[] = path ? [...path, k] : [k];
        fields.push({ name: next, touched: false });
        walk((ref as Record<string, unknown>)[k], next);
      });
    }
  };
  walk(values);
  form.setFields(fields);
}

/** Class-wide subsystems of storage, SAN and legacy (pre HW type) LAN rows. */
export const subTypes = {
  [ClassKey.STORAGE]: [
    { class: ClassKey.SAN, hw_type: ClassKey.STORAGE, label: 'Device', subsystem: 'device' },
    { class: ClassKey.STORAGE, hw_type: ClassKey.STORAGE, label: 'Host', subsystem: 'host' },
    { class: ClassKey.STORAGE, hw_type: ClassKey.STORAGE, label: 'Node', subsystem: 'node' },
    { class: ClassKey.STORAGE, hw_type: ClassKey.STORAGE, label: 'Pool', subsystem: 'pool' },
    { class: ClassKey.STORAGE, hw_type: ClassKey.STORAGE, label: 'Volume', subsystem: 'volume' },
  ],
  [ClassKey.SAN]: [
    { class: ClassKey.SAN, hw_type: ClassKey.SAN, label: 'Device', subsystem: 'device' },
    { class: ClassKey.SAN, hw_type: ClassKey.SAN, label: 'SAN port', subsystem: 'sanport' },
  ],
  [ClassKey.LAN]: [
    { class: ClassKey.LAN, hw_type: ClassKey.LAN, label: 'Device', subsystem: 'device' },
    { class: ClassKey.LAN, hw_type: ClassKey.LAN, label: 'Physical port', subsystem: 'physical' },
    { class: ClassKey.LAN, hw_type: ClassKey.LAN, label: 'Vlan port', subsystem: 'vlan' },
    { class: ClassKey.LAN, hw_type: ClassKey.LAN, label: 'Portchannel', subsystem: 'portchannel' }
  ]
} as const;

export const INTERVAL_OPTIONS: { value: GraphInterval; label: string; }[] = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
];

export interface MetricEntry {
  metric: string;
  type: ChartType;
  stacked: boolean;
  aggregation?: MetricAggregate;
  color?: string;
}

export interface GroupOption {
  dashboard_group_id: string;
  label: string;
}

export interface GraphBuilderFormValues {
  label: string;
  dashboard_group_id: string;
  class?: string;
  hw_type?: string;
  subsystem: string;
  rule: RuleGroup;
  metrics: MetricEntry[];
  per_item: boolean;
  interval: GraphInterval;
  legend: boolean;
}

// Stable row identity: the graph id for saved rows, a client-generated key for new
// ones. Positional indexes are NOT used — deletes would renumber rows, remounting
// their forms (losing unsaved edits) and misrouting in-flight save responses.
export interface IndexedGraph extends DashboardCustomGraphPreview {
  rowKey: string;
  __seed?: Partial<GraphBuilderFormValues>;
}

export interface FormGraph extends Partial<GraphBuilderFormValues> {
  dashboard_custom_graph_id?: string;
  // True once the detail (rule/metrics/per_item) was loaded into the form store —
  // it survives collapse, preventing refetch-clobber and empty-DTO saves.
  __hydrated?: boolean;
}

export const emptyFormValues: Partial<GraphBuilderFormValues> = {
  rule: defaultRule,
  metrics: [],
  per_item: false,
  interval: 'day',
  legend: false,
};

export function customGraphToFormValues(g: DashboardCustomGraph): GraphBuilderFormValues {
  const gFull = g as DashboardCustomGraph & ItemGroupWithLabels;
  const rule: RuleGroup = {
    items: g.items?.map(it => ({ item_id: it.item_id, disabled: false })),
    regex: g.regex,
    tags: g.tags,
    parents: gFull.parents,
    exclude: gFull.exclude,
  };

  return {
    label: g.label,
    dashboard_group_id: g.dashboard_group_id,
    class: g.class,
    hw_type: g.hw_type,
    subsystem: g.subsystem,
    rule,
    per_item: g.per_item ?? false,
    interval: g.interval ?? 'day',
    legend: g.legend ?? false,
    metrics: [...g.metrics]
      .sort((a, b) => b.order - a.order)
      .map(({ metric, type, stacked, aggregation, color }) => ({ metric, type, stacked, aggregation, color })),
  };
}

export function formValuesToDTO(v: GraphBuilderFormValues): CreateDashboardCustomGraphDTO {
  const ruleApi = formatRuleDataForApi(v.rule);

  const parents = ruleApi.parents && {
    intersection: ruleApi.parents.intersection ?? false,
    item_ids: ruleApi.parents.item_ids,
    regex: ruleApi.parents.regex,
    tags: ruleApi.parents.tags,
  };
  const exclude = ruleApi.exclude && {
    item_ids: ruleApi.exclude.item_ids,
    regex: ruleApi.exclude.regex,
    tags: ruleApi.exclude.tags,
  };

  const lastIndex = v.metrics.length - 1;
  const metrics: CustomGraphMetric[] = v.metrics.map((m, index) => ({
    metric: m.metric,
    type: m.type,
    stacked: m.type === CHART_TYPE.area,
    order: lastIndex - index,
    aggregation: v.per_item ? undefined : m.aggregation,
    color: (v.per_item || m.aggregation) ? m.color : undefined,
  }));

  return {
    item_ids: ruleApi.item_ids,
    regex: ruleApi.regex,
    tags: ruleApi.tags,
    parents,
    exclude,
    dashboard_group_id: v.dashboard_group_id,
    label: v.label,
    class: v.class,
    hw_type: ruleHwType(v.class, v.hw_type),
    subsystem: v.subsystem,
    metrics,
    per_item: v.per_item,
    interval: v.interval,
    legend: v.legend,
  };
}
